/**
 * When the same facet appears across multiple saved searches with different values,
 * down-rank contributions from older searches if newer searches explicitly chose a
 * disjoint set of refinements (recency bias on conflicts only).
 */

export type FacetValuesPerSearch = {
  /** 0 = most recent search */
  searchRank: number;
  /** Already combined recency weight for that search */
  searchWeight: number;
  /** Normalised facet values selected in that search (may be empty = no filter) */
  values: Set<string>;
};

const DEFAULT_OLDER_CONFLICT_FACTOR = 0.22;

function sortByRank(snapshots: FacetValuesPerSearch[]): FacetValuesPerSearch[] {
  return [...snapshots].sort((a, b) => a.searchRank - b.searchRank);
}

/**
 * Produces value → effective weight for scoring. Newer searches keep full weight;
 * older values that disagree with any newer non-empty refinement set are multiplied
 * by {@link olderConflictFactor} once per such newer search.
 */
export function mergeFacetValuesWithRecencyConflictBias(
  perSearch: FacetValuesPerSearch[],
  options?: { olderConflictFactor?: number }
): Map<string, number> {
  const olderConflictFactor = options?.olderConflictFactor ?? DEFAULT_OLDER_CONFLICT_FACTOR;
  const sorted = sortByRank(perSearch);
  const out = new Map<string, number>();

  for (let i = 0; i < sorted.length; i++) {
    const snap = sorted[i];
    for (const v of snap.values) {
      if (!v) continue;
      let mult = 1;
      for (let j = 0; j < i; j++) {
        const newer = sorted[j];
        if (newer.values.size === 0) continue;
        if (newer.values.has(v)) continue;
        mult *= olderConflictFactor;
      }
      const add = snap.searchWeight * mult;
      out.set(v, (out.get(v) ?? 0) + add);
    }
  }

  return out;
}
